import { type ButtonHTMLAttributes, type ReactNode } from 'react';

interface PrimaryRitualButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
}

/**
 * Primary CTA button with ritual/mystic styling and press animation
 */
export function PrimaryRitualButton({
  children,
  isLoading = false,
  loadingText = 'Channeling...',
  disabled,
  className = '',
  ...props
}: PrimaryRitualButtonProps) {
  return (
    <button
      disabled={disabled || isLoading}
      className={`
        relative w-full px-6 py-3 overflow-hidden
        bg-gradient-to-r from-rune/20 via-rune/30 to-rune/20
        border border-rune/50 rounded-lg
        text-rune font-display text-lg tracking-wide
        hover:from-rune/30 hover:via-rune/40 hover:to-rune/30
        hover:border-rune hover:text-rune-glow
        active:scale-[0.98] active:from-rune/40 active:via-rune/50 active:to-rune/40
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        transition-all duration-200
        group
        ${className}
      `}
      {...props}
    >
      {/* Glow effect on hover */}
      <span
        className="absolute inset-0 bg-gradient-to-r from-transparent via-rune/10 to-transparent
          translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
        aria-hidden="true"
      />

      {/* Button content */}
      <span className="relative flex items-center justify-center gap-2">
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span>{loadingText}</span>
          </>
        ) : (
          children
        )}
      </span>
    </button>
  );
}

function LoadingSpinner() {
  return (
    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
